import { getGeoInfo } from '@/interfaces/plan/manage';
import { PlanDest } from '@/types/plan';
import { useEffect, useState } from 'react';
import { Listener, Container as MapDiv, Marker, NaverMap } from 'react-naver-maps';
import styled from 'styled-components';
import { CustomTextField } from '../CustomMui';
import { Button, Card, Typography } from '@mui/material';
import { backdropAtom } from '@/commons/constants';
import { useSetAtom } from 'jotai';

const MainWrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const FlootingWrap = styled(Card)`
  opacity: 0.95;
  padding: 1rem;
  z-index: 100;
  position: absolute;
  width: 30%;
  top: 1rem;
  left: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  .row{
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 1rem;
  }
`;

interface CoordMapProps {
  target: PlanDest;
  setInfo: React.Dispatch<React.SetStateAction<PlanDest>>;
  mode?: string;
}

export default function CoordMap({ target: pTarget, setInfo, mode }: CoordMapProps) {
  const setBackdrop = useSetAtom(backdropAtom);
  const [target, setTarget] = useState<PlanDest>(pTarget);
  const [searchAddress, setSearchAddress] = useState<string>(pTarget.address);
  useEffect(() => {
    setTarget(pTarget);
  }, [pTarget]);

  useEffect(() => {
    if (target !== pTarget) {
      if (!mode) {
        setInfo({
          ...pTarget,
          lat: target.lat,
          lng: target.lng,
          sido: target.sido,
          sigugun: target.sigugun,
          dongmyun: target.dongmyun,
          ri: target.ri,
          roadAddress: target.roadAddress,
          jibunAddress: target.jibunAddress,
          postal_code: target.postal_code,
        });
      } else if (mode === 'CARGO') {
        setInfo({
          ...pTarget,
          address: target.address,
          lat: target.lat,
          lng: target.lng,
          sido: target.sido,
          sigugun: target.sigugun,
          dongmyun: target.dongmyun,
          ri: target.ri,
          roadAddress: target.roadAddress,
          jibunAddress: target.jibunAddress,
          postal_code: target.postal_code,
        });
      }
    }
  }, [target.lat, target.lng]);

  const getCoordInfo = async (address: string) => {
    try {
      const info = await getGeoInfo(address);
      if (info.status === 200) {
        if (info.data.data) {
          setTarget({ ...target, ...info.data.data });
          if (mode === 'CARGO') {
            setSearchAddress(address);
          }
        } else {
          alert('검색결과가 없습니다.');
        }
      }
    } finally {
      setBackdrop(false);
    }
  };

  const handleSearchClick = () => {
    setBackdrop(true);
    getCoordInfo(searchAddress);
  };

  const handleMapClick = async (event: any) => {
    setBackdrop(true);
    naver.maps.Service.reverseGeocode(
      {
        coords: event.coord,
        orders: [naver.maps.Service.OrderType.ADDR, naver.maps.Service.OrderType.ROAD_ADDR].join(','),
      },
      (status, response) => {
        if (response.v2.address && response.v2.status.name === 'ok') {
          const address =
            response.v2.address.roadAddress !== '' ? response.v2.address.roadAddress : response.v2.address.jibunAddress;
          getCoordInfo(address);
        } else {
          setBackdrop(false);
        }
      },
    );
  };

  return (
    <MainWrap>
      <FlootingWrap>
        <div className="row">
          <div style={{ flexGrow: 1, width: '50%' }}>
            <Typography variant="h6">주소검색</Typography>
          </div>
          <div className="row">
            <Button variant="contained" size="small" onClick={handleSearchClick}>
              검색
            </Button>
          </div>
        </div>
        <CustomTextField
          id="outlined-basic"
          label="주소"
          variant="outlined"
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
          value={searchAddress}
          onChange={(e) => setSearchAddress(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()}
        />
      </FlootingWrap>
      <MapDiv style={{ width: '100%', height: '100%' }}>
        <NaverMap
          center={target.lat && { lat: target.lat, lng: target.lng }}
          baseTileOpacity={1}
          zoom={19}
          background="white"
          tileSpare={7}
        >
          {target.lat && <Marker zIndex={100} position={{ lat: target.lat, lng: target.lng }} />}
          <Listener type="click" listener={handleMapClick} />
        </NaverMap>
      </MapDiv>
    </MainWrap>
  );
}
